import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { resolvePrescriber, findDoctor, regLine, buildRxWhatsApp } from '../lib/prescription'
import { friendlyError } from '../lib/permissions'

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  teal900: '#0d3d3d', teal800: '#0f5454', teal700: '#126868',
  teal600: '#148080', teal500: '#19a8a8', teal200: '#90e0e0',
  teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red600: '#dc2626',
  green50: '#f0fdf4', green600: '#16a34a',
  amber50: '#fffbeb', amber600: '#d97706',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

// ─── Clinical data ─────────────────────────────────────────────────────────────
const DIAGNOSES = [
  'Acne Vulgaris', 'Melasma', 'Psoriasis', 'Eczema / Atopic Dermatitis',
  'Alopecia Areata', 'Androgenetic Alopecia', 'Rosacea', 'Vitiligo',
  'Fungal Infection (Tinea)', 'Contact Dermatitis', 'Hyperpigmentation',
  'Seborrheic Dermatitis', 'Urticaria', 'Warts', 'Mole / Nevus',
  'Skin Tag', 'Perioral Dermatitis', 'Hidradenitis Suppurativa',
]

const MEDICINES = [
  'Tretinoin 0.025% Cream', 'Tretinoin 0.05% Cream', 'Tretinoin 0.1% Cream',
  'Clindamycin 1% Gel', 'Clindamycin + Benzoyl Peroxide Gel',
  'Adapalene 0.1% Gel', 'Adapalene 0.3% Gel',
  'Benzoyl Peroxide 2.5% Gel', 'Benzoyl Peroxide 5% Gel',
  'Hydroquinone 2% Cream', 'Hydroquinone 4% Cream',
  'Kojic Acid + Glycolic Acid Cream', 'Niacinamide 4% Cream',
  'Azelaic Acid 15% Gel', 'Azelaic Acid 20% Cream',
  'Clobetasol 0.05% Cream', 'Mometasone 0.1% Cream',
  'Tacrolimus 0.03% Ointment', 'Tacrolimus 0.1% Ointment',
  'Ketoconazole 2% Cream', 'Ketoconazole 2% Shampoo',
  'Clotrimazole 1% Cream', 'Terbinafine 1% Cream',
  'Minoxidil 2% Solution', 'Minoxidil 5% Solution', 'Minoxidil 5% Foam',
  'Salicylic Acid 2% Face Wash', 'Salicylic Acid 6% Ointment',
  'Sunscreen SPF 50+ PA++++', 'Moisturiser (Fragrance-Free)',
  'Cetaphil Gentle Skin Cleanser',
  'Azithromycin 250mg', 'Azithromycin 500mg',
  'Doxycycline 100mg', 'Minocycline 50mg',
  'Cetirizine 10mg', 'Fexofenadine 120mg', 'Fexofenadine 180mg',
  'Fluconazole 150mg', 'Itraconazole 100mg',
  'Methotrexate 2.5mg', 'Hydroxychloroquine 200mg',
  'Biotin 5mg', 'Zinc Supplement',
]

const FORMULATIONS   = ['Cream', 'Gel', 'Ointment', 'Lotion', 'Serum', 'Solution', 'Foam', 'Shampoo', 'Tablet', 'Capsule', 'Spray']
const DOSAGES        = ['OD (Once daily)', 'BD (Twice daily)', 'TDS (Thrice daily)', 'QID (Four times daily)', 'SOS (As needed)', 'HS (At bedtime)', 'Apply once daily (morning)', 'Apply once daily (night)', 'Apply twice daily', 'Once weekly', 'Alternate nights']
const DURATIONS      = ['3 days', '5 days', '7 days', '10 days', '14 days', '1 month', '2 months', '3 months', 'Ongoing']
const INSTRUCTIONS   = ['Apply thin layer', 'Apply on affected area only', 'Apply before sunscreen', 'Apply after sunscreen', 'Use with sunscreen', 'Avoid eyes and lips', 'Avoid sun exposure after application', 'Wash off after 15–20 min', 'With food', 'After meals', 'On empty stomach', 'At bedtime', 'Use as directed']
const ADVICE_OPTIONS = ['Use sunscreen daily (SPF 50+)', 'Avoid picking or scratching', 'Drink plenty of water (2–3L/day)', 'Avoid oily and spicy food', 'Use gentle face wash only', 'Avoid makeup till cleared', 'Do not self-medicate', 'Keep affected area clean and dry', 'Avoid harsh scrubbing', 'Use lukewarm water to wash face']
// option values use internal keys; labels shown to users via FOLLOWUP_LABEL map
const FOLLOWUP_LABEL   = { 'as_needed': 'As needed', 'custom': 'Custom date' }

const BLANK_MED = { name: '', formulation: '', dosage: '', duration: '', instructions: '' }

// ─── Helpers ───────────────────────────────────────────────────────────────────
// Local calendar date (toISOString is UTC and gives yesterday before 05:30 IST)
function isoDate(d = new Date()) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '' }
function fmtShort(d) { return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '' }

function addDaysToISO(days) {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return isoDate(d)
}
function followupISO(label) {
  const map = { '1 week': 7, '2 weeks': 14, '1 month': 30, '3 months': 90, '6 months': 180 }
  return map[label] ? addDaysToISO(map[label]) : null
}

// ─── Combobox ──────────────────────────────────────────────────────────────────
function Combobox({ value, onChange, options, placeholder, style }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState(value || '')
  const ref = useRef(null)

  useEffect(() => { setQ(value || '') }, [value])
  useEffect(() => {
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const filtered = options.filter(o => o.toLowerCase().includes(q.toLowerCase())).slice(0, 10)

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <input
        value={q}
        onChange={e => { setQ(e.target.value); onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={{ ...inputSx, width: '100%' }}
        onFocus2={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: T.white, border: `1.5px solid ${T.gray200}`, borderRadius: 9,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 3, maxHeight: 200, overflowY: 'auto',
        }}>
          {filtered.map(o => (
            <div key={o} onMouseDown={() => { onChange(o); setQ(o); setOpen(false) }} style={{
              padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: T.gray800,
              borderBottom: `1px solid ${T.gray50}`,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.teal50}
              onMouseLeave={e => e.currentTarget.style.background = T.white}
            >{o}</div>
          ))}
        </div>
      )}
    </div>
  )
}

const inputSx = {
  padding: '7px 10px', border: `1.5px solid ${T.gray200}`, borderRadius: 7,
  fontSize: 13, color: T.gray900, background: T.white, outline: 'none',
  fontFamily: T.font, boxSizing: 'border-box', width: '100%',
}
function FSelect({ value, onChange, children, style }) {
  return (
    <select value={value} onChange={onChange} style={{ ...inputSx, cursor: 'pointer', ...style }}>{children}</select>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRESCRIPTION PAD
// ═══════════════════════════════════════════════════════════════════════════════
function PrescriptionPad({ patient: initPatient, appointmentId, rxData, readOnly, onSaved, onBack }) {
  const { userProfile, clinic, doctors } = useAuth()
  const [patient, setPatient] = useState(initPatient || null)
  const [chosenDoctor, setChosenDoctor] = useState(rxData?.doctor_name || '')
  // Saved prescriptions keep the doctor they were written by; new ones use the logged-in
  // doctor (if registered) or the doctor picked on the pad.
  const prescriber = readOnly
    ? (findDoctor(doctors, rxData?.doctor_name) || (rxData?.doctor_name ? { full_name: rxData.doctor_name } : null))
    : resolvePrescriber(userProfile, doctors, chosenDoctor)
  const lockedToSelf = !readOnly && !!userProfile?.registration_no
  const [date,    setDate]    = useState(rxData?.created_at ? isoDate(new Date(rxData.created_at)) : isoDate())
  const [chief,   setChief]   = useState(rxData?.chief || '')
  const [findings, setFindings] = useState(rxData?.findings || '')
  const [diagnosis, setDiagnosis] = useState(rxData?.diagnosis || '')
  const [medicines, setMeds] = useState(rxData?.medicines?.length ? rxData.medicines : [{ ...BLANK_MED }])
  const [checkedAdvice, setCheckedAdvice] = useState(rxData?.checkedAdvice || [])
  const [followup, setFollowup] = useState(rxData?.followup || '1 month')
  const [followupDate, setFollowupDate] = useState(rxData?.followup_date || followupISO(rxData?.followup || '1 month') || '')
  const [notes,    setNotes]   = useState(rxData?.notes || '')
  const [patientSearch,      setPatientSearch]  = useState(initPatient?.full_name || '')
  const [patientSuggestions, setPSuggestions]   = useState([])
  const [selectedPatientId,  setSelectedPatientId] = useState(initPatient?.id || null)
  const [loadedBanner,       setLoadedBanner]   = useState('')
  const [saving,   setSaving]  = useState(false)
  const [saved,    setSaved]   = useState(false)
  const [error,    setError]   = useState('')
  const padRef = useRef(null)

  function setMed(i, k, v) { setMeds(m => { const n = [...m]; n[i] = { ...n[i], [k]: v }; return n }) }
  function addMed()    { setMeds(m => [...m, { ...BLANK_MED }]) }
  function removeMed(i) { setMeds(m => m.filter((_, j) => j !== i)) }
  function toggleAdvice(a) { setCheckedAdvice(c => c.includes(a) ? c.filter(x => x !== a) : [...c, a]) }

  async function handlePatientSearch(value) {
    setPatientSearch(value)
    setPatient(null)
    setSelectedPatientId(null)
    if (value.length < 2) { setPSuggestions([]); return }
    const { data } = await supabase.from('patients')
      .select('id, full_name, phone, age')
      .ilike('full_name', `%${value}%`)
      .limit(6)
    setPSuggestions(data || [])
  }

  async function handlePatientSelect(p) {
    setSelectedPatientId(p.id)
    setPatient(p)
    setPatientSearch(p.full_name)
    setPSuggestions([])

    // Step 1: fetch last rx by patient_id
    let { data: rx } = await supabase
      .from('prescriptions')
      .select('*')
      .eq('patient_id', p.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    // Step 2: fallback by patient_name (fuzzy) if no result
    if (!rx && p.full_name) {
      const { data: rx2 } = await supabase
        .from('prescriptions')
        .select('*')
        .ilike('patient_name', `%${p.full_name}%`)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      rx = rx2
    }

    if (!rx) return

    setChief(rx.chief_complaint || '')
    setFindings(rx.examination_findings || '')
    if (rx.diagnosis) setDiagnosis(rx.diagnosis)

    setMeds(
      rx.medicines && rx.medicines.length > 0
        ? rx.medicines
        : [{ ...BLANK_MED }]
    )

    let adv = rx.advice || []
    if (typeof adv === 'string') {
      try { adv = JSON.parse(adv) } catch { adv = ADVICE_OPTIONS.filter(a => adv.includes(a)) }
    }
    setCheckedAdvice(Array.isArray(adv) ? adv : [])

    // Normalise legacy display strings back to internal option keys
    const rawFup = rx.follow_up || '1 month'
    const normFup = rawFup === 'As needed' ? 'as_needed' : rawFup === 'Custom date' ? 'custom' : rawFup
    setFollowup(normFup)
    setFollowupDate(rx.followup_date || followupISO(normFup) || '')
    setNotes(rx.additional_notes || '')

    const visitDate = new Date(rx.created_at).toLocaleDateString('en-IN',
      { day: '2-digit', month: 'short', year: 'numeric' })
    setLoadedBanner(`📋 Loaded from last visit (${visitDate}) — edit as needed`)
    setTimeout(() => setLoadedBanner(''), 5000)
  }

  function handleFollowupChange(val) {
    setFollowup(val)
    if (val === 'as_needed' || val === '' || val === 'custom') setFollowupDate('')
    // 'custom': user sets date via date picker; date-range options: auto-calculate
    else setFollowupDate(followupISO(val) || '')
  }

  function sendFollowUpWhatsApp() {
    const phone = (patient?.phone || '').replace(/\D/g, '')
    if (!phone) { alert('No phone number on record for this patient.'); return }
    const dateStr = fmtDate(followupDate)
    const msg = `Hello ${patient?.full_name || 'Patient'}, this is a reminder from ${clinic?.name || 'the clinic'}. Your follow-up appointment is scheduled for ${dateStr}. ${clinic?.phone ? `Please call us at ${clinic.phone} to confirm.` : 'Please call us to confirm.'} Thank you! 🌿`
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`, '_blank')
  }

  function sendFollowUpEmail() {
    const email = (patient?.email || '').trim()
    if (!email) { alert('No email on record for this patient.'); return }
    const dateStr = fmtDate(followupDate)
    const cName   = clinic?.name || 'the clinic'
    const subject = `Follow-up Reminder - ${cName}`
    const body = `Hello ${patient?.full_name || 'Patient'},\n\nThis is a reminder from ${cName}.\n\nYour follow-up appointment is scheduled for ${dateStr}.\n\nPlease call us${clinic?.phone ? ` at ${clinic.phone}` : ''} to confirm your appointment.\n\nThank you!\n${cName} 🌿`
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  async function handleSave() {
    if (!selectedPatientId) { setError('Please select a patient from the dropdown list.'); return }
    if (!diagnosis.trim()) { setError('Diagnosis is required.'); return }
    if (!prescriber)       { setError('Please choose the prescribing doctor.'); return }
    setError(''); setSaving(true)

    const payload = {
      patient_id:           selectedPatientId,
      patient_name:         patientSearch,
      doctor_name:          prescriber.full_name,
      appointment_id:       appointmentId || null,
      clinic_id:            userProfile?.clinic_id,
      chief_complaint:      chief || null,
      examination_findings: findings || null,
      diagnosis:            diagnosis.trim(),
      medicines:            medicines.filter(m => m.name.trim()),
      advice:               checkedAdvice,
      follow_up:            FOLLOWUP_LABEL[followup] || followup,
      additional_notes:     notes || null,
      followup_date:        followupDate || null,
    }
    try {
      const { data, error } = rxData?.id
        ? await supabase.from('prescriptions').update(payload).eq('id', rxData.id).select().single()
        : await supabase.from('prescriptions').insert(payload).select().single()
      if (error) throw error
      setSaved(true); onSaved && onSaved(data)
    } catch (e) { setError(friendlyError(e)) }
    setSaving(false)
  }

  function handlePrint() {
    const styleId = 'rx-print-style'
    let el = document.getElementById(styleId)
    if (!el) { el = document.createElement('style'); el.id = styleId; document.head.appendChild(el) }
    el.textContent = `
      @page { size: A4; margin: 10mm 12mm; }
      @media print {
        a[href]:after { content: none !important; }
        body * { visibility: hidden; }
        #prescription-print-content,
        #prescription-print-content * { visibility: visible; }
        #prescription-print-content {
          position: absolute; left: 0; top: 0; width: 100%;
          font-size: 10px; line-height: 1.3;
        }
        #prescription-print-content .no-print { display: none !important; }
        #prescription-print-content .print-only { display: inline !important; }
        #prescription-print-content .advice-unchecked { display: none !important; }
        #prescription-print-content .advice-checked input[type="checkbox"] { display: none !important; }
        #prescription-print-content table { font-size: 9px; }
        #prescription-print-content textarea {
          border: none !important; font-size: 10px; resize: none !important;
        }
        #prescription-print-content input,
        #prescription-print-content select {
          border: none !important; font-size: 10px; -webkit-appearance: none; appearance: none;
        }
        #prescription-print-content .signature-section {
          margin-top: 10px;
          page-break-before: avoid !important;
          page-break-inside: avoid !important;
        }
      }
    `
    window.print()
    setTimeout(() => { el.textContent = '' }, 1000)
  }

  function handleWhatsApp() {
    if (!prescriber) { setError('Please choose the prescribing doctor.'); return }
    const text = buildRxWhatsApp({
      clinic, doctor: prescriber,
      patient: { name: patient?.full_name, age: patient?.age },
      date, diagnosis, medicines, advice: checkedAdvice, notes,
      followUp: [FOLLOWUP_LABEL[followup] || followup, followupDate && followup !== 'as_needed' ? `(${fmtDate(followupDate)})` : ''].filter(Boolean).join(' '),
    })
    const phone = (patient?.phone || '').replace(/\D/g, '')
    window.open(phone ? `https://wa.me/91${phone}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
  }

  const isRO = readOnly

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f0f0f0', padding: '20px 16px', fontFamily: T.font }}>

      {/* Toolbar */}
      <div className="no-print" style={{ maxWidth: 780, margin: '0 auto 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ ...toolBtn, color: T.gray600 }}>← Back</button>
        <div style={{ flex: 1 }} />
        {error && <span style={{ fontSize: 13, color: T.red600 }}>⚠️ {error}</span>}
        {saved && <span style={{ fontSize: 13, color: T.green600, fontWeight: 600 }}>✅ Saved</span>}
        {!isRO && <button onClick={handleSave} disabled={saving} style={{ ...toolBtn, background: T.teal600, color: T.white, border: 'none', boxShadow: '0 2px 8px rgba(20,128,128,0.3)' }}>{saving ? 'Saving…' : '💾 Save'}</button>}
        <button onClick={handlePrint} style={{ ...toolBtn }}>🖨️ Print</button>
        <button onClick={handleWhatsApp} style={{ ...toolBtn, borderColor: '#25d366', color: '#128c3e' }}>💬 WhatsApp</button>
      </div>

      {/* ── The prescription pad ── */}
      <div ref={padRef} id="prescription-print-content" style={{
        maxWidth: 780, margin: '0 auto',
        background: T.white, borderRadius: 4,
        boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
        overflow: 'hidden',
      }}>

        {/* ── Letterhead (doctor: user_profiles · clinic: clinics) ── */}
        <div style={{ borderBottom: `4px double ${T.teal600}`, padding: '20px 32px 16px', background: 'linear-gradient(to right, #edfafa, #fff)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.teal900 }}>
                {readOnly || lockedToSelf
                  ? (prescriber?.full_name || '—')
                  : <select value={prescriber?.full_name || ''} onChange={e => setChosenDoctor(e.target.value)} style={{ border: 'none', background: 'transparent', fontSize: 18, fontWeight: 800, color: T.teal900, cursor: 'pointer', fontFamily: T.font, outline: 'none', padding: 0 }}>
                      <option value="">{doctors.length ? 'Select doctor…' : 'No registered doctors'}</option>
                      {doctors.map(d => <option key={d.id} value={d.full_name}>{d.full_name}</option>)}
                    </select>}
              </div>
              {prescriber?.qualification && <div style={{ fontSize: 12, color: T.teal700, fontWeight: 600, marginTop: 2 }}>{prescriber.qualification}</div>}
              {regLine(prescriber) && <div style={{ fontSize: 11.5, color: T.gray600, marginTop: 2 }}>{regLine(prescriber)}</div>}
              <div style={{ fontSize: 11.5, color: T.gray500, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 10 }}>Date</div>
              {!isRO
                ? <input type="date" value={date} onChange={e => setDate(e.target.value)}
                    style={{ border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, color: T.gray900, cursor: 'pointer', fontFamily: T.font, outline: 'none', padding: 0 }} />
                : <div style={{ fontSize: 13, fontWeight: 700, color: T.gray900 }}>{fmtDate(date)}</div>}
            </div>
            <div style={{ textAlign: 'right', maxWidth: 320 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.teal700, fontFamily: 'Georgia, serif' }}>{clinic?.name || ''}</div>
              {clinic?.tagline && <div style={{ fontSize: 11.5, fontWeight: 400, color: T.teal600 }}>{clinic.tagline}</div>}
              {clinic?.address && <div style={{ fontSize: 11.5, color: T.gray500, marginTop: 3, lineHeight: 1.5 }}>{clinic.address}</div>}
              {(clinic?.phone || clinic?.email) && (
                <div style={{ fontSize: 11.5, color: T.gray500, marginTop: 2 }}>{[clinic?.phone && `Ph: ${clinic.phone}`, clinic?.email].filter(Boolean).join(' | ')}</div>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: '18px 32px' }}>

          {/* ── Loaded-from-last-visit banner ── */}
          {loadedBanner && (
            <div style={{ background: '#fffbea', border: '1px solid #f59e0b', borderRadius: 7, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1 }}>{loadedBanner}</span>
              <button onClick={() => setLoadedBanner('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#92400e', lineHeight: 1 }}>×</button>
            </div>
          )}

          {/* ── Patient row ── */}
          <div style={{ background: T.teal50, border: `1px solid ${T.teal100}`, borderRadius: 7, padding: '10px 14px', marginBottom: 18 }}>
            {!isRO && !initPatient ? (
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.teal700, whiteSpace: 'nowrap' }}>Patient</span>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    value={patientSearch}
                    onChange={e => handlePatientSearch(e.target.value)}
                    placeholder="Search patient by name…"
                    style={{ ...inputSx, fontSize: 14, padding: '9px 12px', borderRadius: 8, width: '100%' }}
                  />
                  {patientSuggestions.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.white, border: `1px solid ${T.gray200}`, borderRadius: 8, zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', marginTop: 2 }}>
                      {patientSuggestions.map(p => (
                        <div key={p.id}
                          onMouseDown={e => { e.preventDefault(); handlePatientSelect(p) }}
                          style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: `1px solid ${T.gray100}`, fontSize: 13 }}
                          onMouseEnter={e => e.currentTarget.style.background = T.teal50}
                          onMouseLeave={e => e.currentTarget.style.background = T.white}>
                          <span style={{ fontWeight: 600, color: T.gray900 }}>{p.full_name}</span>
                          <span style={{ color: T.gray400, marginLeft: 8 }}>{p.age}y · {p.phone}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 24, fontSize: 13.5 }}>
                <span><strong style={{ color: T.gray500, fontSize: 11 }}>PATIENT</strong><br /><strong style={{ color: T.gray900, fontSize: 15 }}>{patient?.full_name || '—'}</strong></span>
                {patient?.age  && <span><strong style={{ color: T.gray500, fontSize: 11 }}>AGE</strong><br /><strong style={{ color: T.gray700 }}>{patient.age} yrs</strong></span>}
                {patient?.gender && <span><strong style={{ color: T.gray500, fontSize: 11 }}>GENDER</strong><br /><strong style={{ color: T.gray700 }}>{patient.gender}</strong></span>}
                {patient?.phone  && <span><strong style={{ color: T.gray500, fontSize: 11 }}>PHONE</strong><br /><strong style={{ color: T.gray700 }}>{patient.phone}</strong></span>}
              </div>
            )}
          </div>

          {/* ── Clinical notes ── */}
          <div style={{ marginBottom: 16 }}>
            <SectionLabel>Chief Complaint</SectionLabel>
            {isRO ? <ReadText>{chief}</ReadText> : <textarea value={chief} onChange={e => setChief(e.target.value)} placeholder="Patient's main complaint…" rows={2} style={{ ...areaSx }} />}
          </div>
          <div style={{ marginBottom: 16 }}>
            <SectionLabel>Examination Findings</SectionLabel>
            {isRO ? <ReadText>{findings}</ReadText> : <textarea value={findings} onChange={e => setFindings(e.target.value)} placeholder="Clinical findings on examination…" rows={2} style={{ ...areaSx }} />}
          </div>
          <div style={{ marginBottom: 20 }}>
            <SectionLabel>Diagnosis</SectionLabel>
            {isRO ? <ReadText style={{ fontWeight: 700, fontSize: 14 }}>{diagnosis}</ReadText>
              : <Combobox value={diagnosis} onChange={setDiagnosis} options={DIAGNOSES} placeholder="Type or select diagnosis…" style={{ maxWidth: 380 }} />}
          </div>

          {/* ── Rx ── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 46, fontFamily: 'Georgia, Times New Roman, serif', color: T.teal700, lineHeight: 0.9, fontStyle: 'italic' }}>℞</span>
              <div style={{ flex: 1, height: 2, background: T.teal100 }} />
            </div>

            {/* Medicine table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: T.teal800 }}>
                  {['#', 'Medicine', 'Formulation', 'Dosage', 'Duration', 'Instructions', !isRO && ''].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: '7px 8px', textAlign: 'left', color: T.white, fontWeight: 600, fontSize: 11, letterSpacing: '0.3px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {medicines.map((med, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.gray100}`, background: i % 2 === 0 ? T.white : T.gray50 }}>
                    <td style={{ padding: '6px 8px', color: T.gray400, fontWeight: 700, width: 28 }}>{i + 1}.</td>
                    <td style={{ padding: '4px 6px', minWidth: 160 }}>
                      {isRO ? <span style={{ fontWeight: 600 }}>{med.name}</span>
                        : <Combobox value={med.name} onChange={v => setMed(i, 'name', v)} options={MEDICINES} placeholder="Medicine name…" />}
                    </td>
                    <td style={{ padding: '4px 6px', width: 110 }}>
                      {isRO ? <span>{med.formulation}</span>
                        : <FSelect value={med.formulation} onChange={e => setMed(i, 'formulation', e.target.value)}>
                            <option value="">—</option>
                            {FORMULATIONS.map(f => <option key={f} value={f}>{f}</option>)}
                          </FSelect>}
                    </td>
                    <td style={{ padding: '4px 6px', width: 120 }}>
                      {isRO ? <span>{med.dosage}</span>
                        : <FSelect value={med.dosage} onChange={e => setMed(i, 'dosage', e.target.value)} style={{ height: 32 }}>
                            <option value="">— Dosage —</option>
                            {DOSAGES.map(d => <option key={d} value={d}>{d}</option>)}
                          </FSelect>}
                    </td>
                    <td style={{ padding: '4px 6px', width: 120 }}>
                      {isRO ? <span>{med.duration}</span>
                        : <FSelect value={med.duration} onChange={e => setMed(i, 'duration', e.target.value)} style={{ height: 32 }}>
                            <option value="">— Duration —</option>
                            {DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                          </FSelect>}
                    </td>
                    <td style={{ padding: '4px 6px', minWidth: 160 }}>
                      {isRO ? <span style={{ color: T.gray600, fontStyle: 'italic' }}>{med.instructions}</span>
                        : <Combobox value={med.instructions} onChange={v => setMed(i, 'instructions', v)} options={INSTRUCTIONS} placeholder="Instructions…" />}
                    </td>
                    {!isRO && (
                      <td style={{ padding: '4px 6px', width: 32 }}>
                        {medicines.length > 1 && (
                          <button onClick={() => removeMed(i)} style={{ background: 'none', border: 'none', color: T.red600, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {!isRO && (
              <button className="no-print" onClick={addMed} style={{
                marginTop: 8, padding: '6px 14px', borderRadius: 7,
                border: `1.5px dashed ${T.teal400}`, background: T.teal50,
                color: T.teal700, fontSize: 12.5, fontWeight: 600,
                cursor: 'pointer', fontFamily: T.font,
              }}>+ Add Medicine</button>
            )}
          </div>

          {/* ── Advice ── */}
          <div style={{ border: `1px dashed ${T.teal300}`, borderRadius: 8, padding: '14px 16px', marginBottom: 18, background: '#fafffe' }}>
            <SectionLabel>Advice & Instructions</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', marginBottom: 12 }}>
              {ADVICE_OPTIONS.map(a => {
                const checked = checkedAdvice.includes(a)
                return (
                  <label key={a} className={checked ? 'advice-checked' : 'advice-unchecked'} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.gray700, cursor: isRO ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={() => !isRO && toggleAdvice(a)}
                      style={{ accentColor: T.teal600, width: 14, height: 14 }} />
                    {a}
                  </label>
                )
              })}
            </div>
            {/* ── Follow-up: native select + date display + reminder buttons ── */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.gray700, whiteSpace: 'nowrap' }}>Follow up in</span>
                {isRO
                  ? <span style={{ fontSize: 13, fontWeight: 700, color: T.teal700 }}>
                      {FOLLOWUP_LABEL[followup] || followup}
                      {followupDate && followup !== 'as_needed' && ` · ${fmtDate(followupDate)}`}
                    </span>
                  : <>
                      {/* Native <select> — FSelect passes onChange as-is causing event-object bug */}
                      <span className="no-print">
                        <select
                          value={followup}
                          onChange={e => handleFollowupChange(e.target.value)}
                          style={{ ...inputSx, width: 170, cursor: 'pointer' }}>
                          <option value="">Select…</option>
                          <option value="1 week">1 week</option>
                          <option value="2 weeks">2 weeks</option>
                          <option value="1 month">1 month</option>
                          <option value="3 months">3 months</option>
                          <option value="6 months">6 months</option>
                          <option value="as_needed">As needed</option>
                          <option value="custom">Custom date</option>
                        </select>
                      </span>
                      <span className="print-only" style={{ display: 'none', fontSize: 13, fontWeight: 700, color: T.teal700 }}>
                        {FOLLOWUP_LABEL[followup] || followup}{followupDate && followup !== 'as_needed' && ` · ${fmtDate(followupDate)}`}
                      </span>
                    </>}
              </div>
              {!isRO && (
                <>
                  {followup === 'custom' && (
                    <input type="date" value={followupDate} min={isoDate()}
                      onChange={e => setFollowupDate(e.target.value)}
                      style={{ ...inputSx, width: 200, marginBottom: 8 }} />
                  )}
                  {followupDate && followup !== 'as_needed' && (
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      📅 Follow-up on: {fmtDate(followupDate)}
                    </div>
                  )}
                  {followupDate && followup !== 'as_needed' && (
                    <div className="no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" onClick={sendFollowUpWhatsApp} style={{
                        padding: '6px 13px', borderRadius: 7, border: '1.5px solid #22c55e',
                        background: '#f0fdf4', color: '#15803d', fontSize: 12.5, fontWeight: 700,
                        cursor: 'pointer', fontFamily: T.font, display: 'flex', alignItems: 'center', gap: 5,
                      }}>📱 Send WhatsApp Reminder</button>
                      <button type="button" onClick={sendFollowUpEmail} style={{
                        padding: '6px 13px', borderRadius: 7, border: `1.5px solid ${T.gray200}`,
                        background: T.white, color: T.teal700, fontSize: 12.5, fontWeight: 700,
                        cursor: 'pointer', fontFamily: T.font, display: 'flex', alignItems: 'center', gap: 5,
                      }}>📧 Send Email Reminder</button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: T.gray500, marginBottom: 4 }}>Additional Notes</div>
              {isRO ? <ReadText>{notes}</ReadText>
                : <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional instructions, warnings…" rows={2} style={{ ...areaSx }} />}
            </div>
          </div>

          {/* ── Footer / signature ── */}
          <div className="signature-section" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 56, paddingTop: 12 }}>
            <div style={{ width: 240, textAlign: 'center', borderTop: `1px solid ${T.gray700}`, paddingTop: 6 }}>
              <div style={{ fontSize: 12.5, color: T.gray900, fontWeight: 700 }}>{prescriber?.full_name || ''}</div>
              {prescriber?.qualification && <div style={{ fontSize: 11, color: T.gray500 }}>{prescriber.qualification}</div>}
              {regLine(prescriber) && <div style={{ fontSize: 10.5, color: T.gray500 }}>{regLine(prescriber)}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom toolbar */}
      <div className="no-print" style={{ maxWidth: 780, margin: '14px auto 0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        {!isRO && <button onClick={handleSave} disabled={saving} style={{ ...toolBtn, background: T.teal600, color: T.white, border: 'none', boxShadow: '0 2px 8px rgba(20,128,128,0.3)' }}>{saving ? 'Saving…' : '💾 Save Prescription'}</button>}
        <button onClick={handlePrint} style={toolBtn}>🖨️ Print</button>
        <button onClick={handleWhatsApp} style={{ ...toolBtn, borderColor: '#25d366', color: '#128c3e' }}>💬 Send via WhatsApp</button>
      </div>
    </div>
  )
}

const toolBtn = {
  padding: '8px 18px', borderRadius: 8, border: `1.5px solid ${T.gray200}`,
  background: T.white, color: T.gray700, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: T.font,
}
const areaSx = {
  width: '100%', padding: '8px 10px', border: `1.5px solid ${T.gray200}`,
  borderRadius: 7, fontSize: 13, color: T.gray900, fontFamily: T.font,
  resize: 'vertical', outline: 'none', background: T.white, boxSizing: 'border-box',
}
function SectionLabel({ children }) {
  return <div style={{ fontSize: 10.5, fontWeight: 700, color: T.gray400, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 5 }}>{children}</div>
}
function ReadText({ children, style }) {
  return children ? <div style={{ fontSize: 13.5, color: T.gray700, lineHeight: 1.6, ...style }}>{children}</div> : <div style={{ fontSize: 13, color: T.gray300, fontStyle: 'italic' }}>—</div>
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRESCRIPTIONS LIST
// ═══════════════════════════════════════════════════════════════════════════════
function PrescriptionsList({ onNew, onView }) {
  const { can, isAdmin } = useAuth()
  const [prescriptions, setPrescriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('prescriptions')
      .select('*, patients(full_name, age, gender, phone)')
      .order('created_at', { ascending: false })
    setPrescriptions(data || [])
    setLoading(false)
  }, [])
  useEffect(() => { load() }, [load])

  async function handleDelete(id) {
    if (!window.confirm('Delete this prescription?')) return
    const { error } = await supabase.from('prescriptions').delete().eq('id', id)
    if (error) { alert(friendlyError(error)); return }
    setPrescriptions(p => p.filter(r => r.id !== id))
  }

  const filtered = prescriptions.filter(p => {
    const q = search.toLowerCase()
    return !q || p.patients?.full_name?.toLowerCase().includes(q) || p.diagnosis?.toLowerCase().includes(q)
  })

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: T.font }}>
      {/* Toolbar */}
      <div style={{ background: T.white, borderBottom: `1px solid ${T.gray200}`, padding: '16px 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.gray900 }}>Prescriptions</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.gray400 }}>{loading ? 'Loading…' : `${filtered.length} prescription${filtered.length !== 1 ? 's' : ''}`}</p>
          </div>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.gray400 }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by patient or diagnosis…"
              style={{ ...inputSx, paddingLeft: 30, width: 240 }}
              onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
              onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }} />
          </div>
          {can('prescriptions.write') && <button onClick={onNew} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8,
            border: 'none', background: T.teal600, color: T.white, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: T.font, boxShadow: '0 2px 8px rgba(20,128,128,0.3)',
          }}>＋ New Prescription</button>}
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: T.gray400, fontSize: 14 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.gray600, marginBottom: 6 }}>{search ? 'No prescriptions match your search' : 'No prescriptions yet'}</div>
            {!search && can('prescriptions.write') && <button onClick={onNew} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>Write First Prescription</button>}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
              <tr style={{ background: T.gray100 }}>
                {['Patient', 'Age / Gender', 'Diagnosis', 'Medicines', 'Follow-up Date', 'Date Written', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.gray500, letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: `2px solid ${T.gray200}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((rx, i) => (
                <tr key={rx.id}
                  style={{ background: i % 2 === 0 ? T.white : T.gray50, borderBottom: `1px solid ${T.gray100}`, cursor: 'pointer', transition: 'background 0.1s' }}
                  onClick={() => onView(rx)}
                  onMouseEnter={e => e.currentTarget.style.background = T.teal50 + '77'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.white : T.gray50}
                >
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: T.gray900 }}>{rx.patients?.full_name || '—'}</td>
                  <td style={{ padding: '12px 16px', color: T.gray500 }}>{[rx.patients?.age && `${rx.patients.age}y`, rx.patients?.gender].filter(Boolean).join(' · ') || '—'}</td>
                  <td style={{ padding: '12px 16px', color: T.gray700, fontWeight: 500 }}>{rx.diagnosis}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: T.teal50, color: T.teal700, border: `1px solid ${T.teal100}`, borderRadius: 20, padding: '2px 10px', fontSize: 11.5, fontWeight: 700 }}>
                      {(rx.medicines || []).length} med{(rx.medicines || []).length !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: rx.followup_date ? T.teal700 : T.gray300, fontWeight: rx.followup_date ? 600 : 400 }}>
                    {rx.followup_date ? fmtShort(rx.followup_date) : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: T.gray500 }}>{fmtShort(rx.created_at)}</td>
                  <td style={{ padding: '12px 12px' }}>
                    <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => onView(rx)} style={{ ...actionBtn }}>👁 View</button>
                      {isAdmin && <button onClick={() => handleDelete(rx.id)} style={{ ...actionBtn, borderColor: '#fecdd3', color: T.red600 }}>🗑</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const actionBtn = { padding: '5px 10px', borderRadius: 6, border: `1px solid ${T.gray200}`, background: T.white, color: T.gray500, fontSize: 12, cursor: 'pointer', fontFamily: T.font }

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PRESCRIPTIONS MODULE
// ═══════════════════════════════════════════════════════════════════════════════
export default function Prescriptions({ patientProp }) {
  const [view, setView]   = useState(patientProp ? 'pad' : 'list') // 'list' | 'pad'
  const [rxData, setRxData] = useState(null)

  function openNew()   { setRxData(null); setView('pad') }
  function openView(rx) {
    // Parse advice: stored as JSON array or legacy text string
    let adv = rx.advice || []
    if (typeof adv === 'string') {
      try { adv = JSON.parse(adv) } catch { adv = ADVICE_OPTIONS.filter(a => adv.includes(a)) }
    }
    const checkedAdvice = Array.isArray(adv) ? adv : []

    // Legacy text field fallbacks (old records stored everything in advice text)
    const adviceText    = typeof rx.advice === 'string' ? rx.advice : ''
    const followupMatch = adviceText.match(/Follow up: ([^\n]+)/)
    const notesMatch    = adviceText.match(/Notes: ([^\n]+)/)

    setRxData({
      ...rx,
      // Use dedicated DB columns first; fall back to legacy text extraction
      chief:    rx.chief_complaint      || '',
      findings: rx.examination_findings || '',
      checkedAdvice,
      followup: rx.follow_up || followupMatch?.[1] || '1 month',
      notes:    rx.additional_notes || notesMatch?.[1] || '',
    })
    setView('pad')
  }

  if (view === 'pad') return (
    <PrescriptionPad
      patient={patientProp || (rxData?.patients ? { ...rxData.patients, id: rxData.patient_id } : null)}
      rxData={rxData}
      readOnly={!!rxData?.id && !patientProp}
      onSaved={() => { setView('list'); setRxData(null) }}
      onBack={() => setView('list')}
    />
  )

  return <PrescriptionsList onNew={openNew} onView={openView} />
}
